<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WabaCallingAPI from './api';
import { useWabaCallingStore } from './store';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
  chat: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const { currentAccount } = useAccount();
const callingStore = useWabaCallingStore();
const configured = ref(false);
const loading = ref(true);
const requesting = ref(false);
const canRequestPermission = ref(false);

const available = computed(
  () =>
    currentAccount.value?.feature_modules?.whatsapp_waba_calls === true &&
    props.inbox?.channel_type === 'Channel::Whatsapp' &&
    props.inbox?.provider === 'whatsapp_cloud' &&
    configured.value
);
const contactId = computed(() => props.chat?.meta?.sender?.id);

const load = async () => {
  if (!props.inbox?.id || !contactId.value) return;

  loading.value = true;
  try {
    const { data } = await WabaCallingAPI.settings(props.inbox.id);
    configured.value = data.enabled === true;
    if (configured.value) {
      const capability = await WabaCallingAPI.capability(
        props.inbox.id,
        contactId.value
      );
      canRequestPermission.value =
        capability.data.can_request_permission === true;
    }
  } catch (error) {
    configured.value = false;
    canRequestPermission.value = false;
  } finally {
    loading.value = false;
  }
};

const requestPermission = async () => {
  requesting.value = true;
  const result = await callingStore.requestPermission(
    props.inbox.id,
    contactId.value
  );
  requesting.value = false;

  if (result.permission) {
    canRequestPermission.value = false;
    useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.PERMISSION_REQUESTED'));
  } else if (result.error === 'call_permission_already_granted') {
    useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.PERMISSION_ALREADY_GRANTED'));
  } else if (result.error === 'call_permission_rate_limited') {
    useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.PERMISSION_RATE_LIMITED'));
  } else {
    useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.PERMISSION_REQUEST_FAILED'));
  }
};

// The header can render before Vuex resolves the inbox. Reload when it arrives.
watch(
  () => [props.inbox?.id, contactId.value],
  ([inboxId, currentContactId]) => {
    if (inboxId && currentContactId) load();
  },
  { immediate: true }
);
</script>

<template>
  <NextButton
    v-show="available && canRequestPermission"
    v-tooltip.bottom="t('ASTRACHAT_WABA_CALLING.ACTION.REQUEST_PERMISSION')"
    sm
    ghost
    slate
    icon="i-lucide-shield-check"
    :is-loading="loading || requesting"
    :disabled="!contactId || requesting"
    @click="requestPermission"
  />
</template>
